import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import ChevronDownSm from "@/assets/icons/ChevronDownSm.svg";
import SearchSm from "@/assets/icons/SearchSm.svg";
import Checkmark from "@/assets/icons/Checkmark.svg";
import { API_URL } from "@/env";
import { showCustomToast } from "../CustomToast";

interface JiraIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  reproductionSteps?: string;
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

interface JiraIssueType {
  id: string;
  name: string;
  iconUrl?: string;
  subtask?: boolean;
}

interface JiraUser {
  accountId: string;
  displayName: string;
  emailAddress?: string;
  avatarUrls?: {
    "48x48": string;
  };
}

interface JiraIssue {
  key: string;
  fields: {
    summary: string;
  };
}

interface JiraPriority {
  id: string;
  name: string;
  iconUrl?: string;
}

interface JiraSite {
  id: string;
  name: string;
  url: string;
  scopes: string[];
  avatarUrl?: string;
}

const CircleLoader = () => (
  <div className="flex items-center justify-center py-4">
    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
  </div>
);

const JiraIssueModal = ({
  isOpen,
  onClose,
  reportId,
  reproductionSteps = "",
}: JiraIssueModalProps) => {
  const { currentWorkspace } = useWorkspaceStore();
  const [sites, setSites] = useState<JiraSite[]>([]);
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [issueTypes, setIssueTypes] = useState<JiraIssueType[]>([]);
  const [users, setUsers] = useState<JiraUser[]>([]);
  const [parentIssues, setParentIssues] = useState<JiraIssue[]>([]);
  const [priorities, setPriorities] = useState<JiraPriority[]>([]);
  const [creating, setCreating] = useState(false);

  // Loading states for each dropdown
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingIssueTypes, setLoadingIssueTypes] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingParentIssues, setLoadingParentIssues] = useState(false);
  const [loadingPriorities, setLoadingPriorities] = useState(false);
  const summaryRef = React.useRef<HTMLTextAreaElement>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    siteId: "",
    projectKey: "",
    issueTypeName: "",
    summary: "",
    description: "",
    assigneeId: "",
    parentIssue: "",
    priority: "",
  });

  const [dropdownPosition, setDropdownPosition] = useState<{
    [key: string]: "top" | "bottom";
  }>({});

  // Refs for dropdown containers
  const siteDropdownRef = useRef<HTMLDivElement>(null);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const issueTypeDropdownRef = useRef<HTMLDivElement>(null);
  const parentIssueDropdownRef = useRef<HTMLDivElement>(null);
  const priorityDropdownRef = useRef<HTMLDivElement>(null);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !openDropdown) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen, onClose, openDropdown]);

  // Handle outside clicks for dropdowns
  // Handle outside clicks and positioning for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const dropdownRefs = {
        site: siteDropdownRef,
        project: projectDropdownRef,
        issueType: issueTypeDropdownRef,
        parentIssue: parentIssueDropdownRef,
        priority: priorityDropdownRef,
        assignee: assigneeDropdownRef,
      };

      const currentRef = openDropdown
        ? dropdownRefs[openDropdown as keyof typeof dropdownRefs]
        : null;

      if (currentRef?.current && !currentRef.current.contains(target)) {
        setOpenDropdown(null);
        setSearchQuery("");
      }
    };

    const checkDropdownPosition = () => {
      if (!openDropdown) return;

      const dropdownRefs = {
        site: siteDropdownRef,
        project: projectDropdownRef,
        issueType: issueTypeDropdownRef,
        parentIssue: parentIssueDropdownRef,
        priority: priorityDropdownRef,
        assignee: assigneeDropdownRef,
      };

      const currentRef =
        dropdownRefs[openDropdown as keyof typeof dropdownRefs];
      if (currentRef?.current) {
        const button = currentRef.current.querySelector("button");
        if (button) {
          const rect = button.getBoundingClientRect();
          const dropdownHeight = 172; // max-h-[172px]
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;

          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setDropdownPosition((prev) => ({ ...prev, [openDropdown]: "top" }));
          } else {
            setDropdownPosition((prev) => ({
              ...prev,
              [openDropdown]: "bottom",
            }));
          }
        }
      }
    };

    if (openDropdown) {
      checkDropdownPosition();
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openDropdown]);

  // Auto-resize summary textarea
  const handleSummaryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, summary: e.target.value });

    // Reset height to minHeight to get the correct scrollHeight
    e.target.style.height = "28px";
    // Set height to scrollHeight to fit content (minimum 28px)
    e.target.style.height = `${Math.max(e.target.scrollHeight, 28)}px`;
  };

  // Reset textarea height when modal opens and populate description with reproduction steps
  useEffect(() => {
    if (isOpen && summaryRef.current) {
      summaryRef.current.style.height = "28px";
    }
    if (isOpen && reproductionSteps) {
      setFormData((prev) => ({
        ...prev,
        description: reproductionSteps,
      }));
    }
  }, [isOpen, reproductionSteps]);

  // Fetch sites when modal opens
  useEffect(() => {
    if (isOpen && currentWorkspace) {
      fetchSites();
    }
  }, [isOpen, currentWorkspace]);

  // Fetch projects and priorities when site changes
  useEffect(() => {
    if (formData.siteId && currentWorkspace) {
      fetchProjects(formData.siteId);
      fetchPriorities(formData.siteId);
    }
  }, [formData.siteId, currentWorkspace]);

  // Fetch issue types and users when project changes
  useEffect(() => {
    if (formData.projectKey && formData.siteId && currentWorkspace) {
      fetchIssueTypes(formData.projectKey, formData.siteId);
      fetchUsers(formData.projectKey, formData.siteId);
    }
  }, [formData.projectKey, formData.siteId, currentWorkspace]);

  // Fetch parent issues when issue type is subtask
  useEffect(() => {
    const selectedIssueType = issueTypes.find(
      (type) => type.name === formData.issueTypeName
    );
    if (
      selectedIssueType?.subtask &&
      formData.projectKey &&
      formData.siteId &&
      currentWorkspace
    ) {
      fetchParentIssues(formData.projectKey, formData.siteId);
    } else {
      setParentIssues([]);
      setFormData((prev) => ({ ...prev, parentIssue: "" }));
    }
  }, [
    formData.issueTypeName,
    formData.projectKey,
    formData.siteId,
    currentWorkspace,
    issueTypes,
  ]);

  const fetchSites = async () => {
    setLoadingSites(true);
    try {
      const response = await fetch(
        `${API_URL}/jira/${currentWorkspace?._id}/sites`
      );

      if (!response.ok) throw new Error("Failed to fetch sites");

      const data = await response.json();
      setSites(data);

      // Auto-select if only one site
      if (data.length === 1) {
        setFormData((prev) => ({ ...prev, siteId: data[0].id }));
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      toast.error("Failed to load Jira sites");
    } finally {
      setLoadingSites(false);
    }
  };

  const fetchProjects = async (siteId: string) => {
    setLoadingProjects(true);
    try {
      const response = await fetch(
        `${API_URL}/jira/${currentWorkspace?._id}/sites/${siteId}/projects`
      );

      if (!response.ok) throw new Error("Failed to fetch projects");

      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load Jira projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchPriorities = async (siteId: string) => {
    setLoadingPriorities(true);
    try {
      const response = await fetch(
        `${API_URL}/jira/${currentWorkspace?._id}/sites/${siteId}/priorities`
      );

      if (!response.ok) throw new Error("Failed to fetch priorities");

      const data = await response.json();
      setPriorities(data);
    } catch (error) {
      console.error("Error fetching priorities:", error);
      // Set default priorities on error
      setPriorities([
        { id: "5", name: "Lowest" },
        { id: "4", name: "Low" },
        { id: "3", name: "Medium" },
        { id: "2", name: "High" },
        { id: "1", name: "Highest" },
      ]);
    } finally {
      setLoadingPriorities(false);
    }
  };

  const fetchIssueTypes = async (projectKey: string, siteId: string) => {
    setLoadingIssueTypes(true);
    try {
      const response = await fetch(
        `${API_URL}/jira/${currentWorkspace?._id}/sites/${siteId}/projects/${projectKey}/issue-types`
      );

      if (!response.ok) throw new Error("Failed to fetch issue types");

      const data = await response.json();

      // If no issue types returned, use default Jira issue types
      if (!data || data.length === 0) {
        const defaultIssueTypes: JiraIssueType[] = [
          { id: "Task", name: "Task" },
          { id: "Bug", name: "Bug" },
          { id: "Story", name: "Story" },
          { id: "Epic", name: "Epic" },
        ];
        setIssueTypes(defaultIssueTypes);
      } else {
        // Map to use name as id for simplicity
        setIssueTypes(
          data.map((type: any) => ({
            id: type.name,
            name: type.name,
            iconUrl: type.iconUrl,
            subtask: type.subtask,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching issue types:", error);
      // Set default issue types on error
      const defaultIssueTypes: JiraIssueType[] = [
        { id: "Task", name: "Task" },
        { id: "Bug", name: "Bug" },
        { id: "Story", name: "Story" },
        { id: "Epic", name: "Epic" },
      ];
      setIssueTypes(defaultIssueTypes);
      toast.error("Failed to load issue types, using defaults");
    } finally {
      setLoadingIssueTypes(false);
    }
  };

  const fetchUsers = async (projectKey: string, siteId: string) => {
    setLoadingUsers(true);
    try {
      const response = await fetch(
        `${API_URL}/jira/${currentWorkspace?._id}/sites/${siteId}/projects/${projectKey}/users`
      );

      if (!response.ok) throw new Error("Failed to fetch users");

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load assignable users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchParentIssues = async (projectKey: string, siteId: string) => {
    setLoadingParentIssues(true);
    try {
      const response = await fetch(
        `${API_URL}/jira/${currentWorkspace?._id}/sites/${siteId}/projects/${projectKey}/tasks`
      );

      if (!response.ok) throw new Error("Failed to fetch parent issues");

      const data = await response.json();
      setParentIssues(data);
    } catch (error) {
      console.error("Error fetching parent issues:", error);
      toast.error("Failed to load parent issues");
    } finally {
      setLoadingParentIssues(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.siteId ||
      !formData.projectKey ||
      !formData.issueTypeName ||
      !formData.summary
    ) {
      showCustomToast("Please fill in all required fields", "exclamation");
      return;
    }

    // Check if subtask requires parent issue
    const selectedIssueType = issueTypes.find(
      (type) => type.name === formData.issueTypeName
    );
    if (selectedIssueType?.subtask && !formData.parentIssue) {
      showCustomToast("Subtask requires a parent issue", "exclamation");
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(
        `${API_URL}/jira/${currentWorkspace?._id}/sites/${formData.siteId}/create-issue`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reportId,
            projectKey: formData.projectKey,
            issueTypeName: formData.issueTypeName,
            summary: formData.summary,
            description: formData.description,
            assigneeId: formData.assigneeId || undefined,
            parentIssue: formData.parentIssue || undefined,
            priority: formData.priority || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response:", errorData);
        throw new Error("Failed to create issue");
      }

      const data = await response.json();

      // Copy issue URL to clipboard if available
      if (data.url) {
        await navigator.clipboard.writeText(data.url);
        showCustomToast(
          "Jira issue created! Link copied to clipboard",
          "check"
        );
      } else {
        showCustomToast("Jira issue created successfully!", "check");
      }
      onClose();

      // Reset form
      setFormData({
        siteId: "",
        projectKey: "",
        issueTypeName: "",
        summary: "",
        description: "",
        assigneeId: "",
        parentIssue: "",
        priority: "",
      });
    } catch (error) {
      console.error("Error creating issue:", error);
      toast.error("Failed to create Jira issue");
    } finally {
      setCreating(false);
    }
  };

  const selectedIssueType = issueTypes.find(
    (type) => type.name === formData.issueTypeName
  );
  const isSubtask = selectedIssueType?.subtask;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="h-[640px] flex flex-col gap-[32px] w-[480px] p-[20px] bg-gray-200 rounded-[24px] max-w-[520px] mx-4 overflow-visible"
          >
            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-[20px] h-full"
            >
              <div className="h-full overflow-y-auto no-scrollbar">
                {/* Summary Textarea */}
                <textarea
                  ref={summaryRef}
                  placeholder="Enter issue summary..."
                  value={formData.summary}
                  onChange={handleSummaryChange}
                  className="!outline-none resize-none bg-transparent w-full text-white text-[20px] leading-[28px] font-medium placeholder:text-white/[0.32] overflow-hidden"
                  rows={1}
                />

                {/* Description Textarea */}
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Description"
                  className="!outline-none mt-4 resize-none no-scrollbar h-full bg-transparent w-full text-white text-[14px] leading-[20px] placeholder:text-white/[0.32]"
                />
              </div>

              <div className="flex flex-col gap-[2px]">
                {/* Site Selection - Custom Dropdown */}
                <div className="relative shrink-0" ref={siteDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDropdown(openDropdown === "site" ? null : "site");
                      setSearchQuery("");
                    }}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors"
                  >
                    <p className="text-left">Site</p>
                    <div className="flex items-center gap-2 justify-between">
                      <span className="text-white truncate">
                        {formData.siteId
                          ? sites.find((s) => s.id === formData.siteId)?.name
                          : "Select"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "site" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.site === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search site"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingSites ? (
                            <CircleLoader />
                          ) : (
                            sites
                              .filter(
                                (site) =>
                                  site.name
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  site.url
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase())
                              )
                              .map((site) => (
                                <button
                                  key={site.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      siteId: site.id,
                                      projectKey: "",
                                      issueTypeName: "",
                                      assigneeId: "",
                                      parentIssue: "",
                                    });
                                    setProjects([]);
                                    setIssueTypes([]);
                                    setUsers([]);
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-gray-500 pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.siteId === site.id
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{site.name}</span>
                                  {formData.siteId === site.id && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Project Selection - Custom Dropdown */}
                <div className="relative shrink-0" ref={projectDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.siteId) {
                        toast.error("Please select a site first");
                        return;
                      }
                      setOpenDropdown(
                        openDropdown === "project" ? null : "project"
                      );
                      setSearchQuery("");
                    }}
                    disabled={!formData.siteId}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <p className="text-left">Project</p>
                    <div className="flex items-center gap-2 justify-between">
                      <span className="text-white truncate">
                        {formData.projectKey
                          ? projects.find((p) => p.key === formData.projectKey)
                              ?.name
                          : "Select"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "project" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.project === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search team"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingProjects ? (
                            <CircleLoader />
                          ) : (
                            projects
                              .filter(
                                (project) =>
                                  project.name
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase()) ||
                                  project.key
                                    .toLowerCase()
                                    .includes(searchQuery.toLowerCase())
                              )
                              .map((project) => (
                                <button
                                  key={project.key}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      projectKey: project.key,
                                      issueTypeName: "",
                                      assigneeId: "",
                                      parentIssue: "",
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-gray-500 pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.projectKey === project.key
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{project.name}</span>
                                  {formData.projectKey === project.key && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Issue Type Selection - Custom Dropdown */}
                <div className="relative shrink-0" ref={issueTypeDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.projectKey) {
                        toast.error("Please select a project first");
                        return;
                      }
                      setOpenDropdown(
                        openDropdown === "issueType" ? null : "issueType"
                      );
                      setSearchQuery("");
                    }}
                    disabled={!formData.projectKey}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <p className="text-left">Issue Type</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white truncate">
                        {formData.issueTypeName || "Select"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "issueType" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.issueType === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search issue type"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingIssueTypes ? (
                            <CircleLoader />
                          ) : (
                            issueTypes
                              .filter((type) =>
                                type.name
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase())
                              )
                              .map((type) => (
                                <button
                                  key={type.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      issueTypeName: type.name,
                                      parentIssue: "",
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-gray-500 pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.issueTypeName === type.name
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{type.name}</span>
                                  {formData.issueTypeName === type.name && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Parent Issue Selection - Only shown for subtasks */}
                {isSubtask && (
                  <div
                    className="relative shrink-0"
                    ref={parentIssueDropdownRef}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setOpenDropdown(
                          openDropdown === "parentIssue" ? null : "parentIssue"
                        );
                        setSearchQuery("");
                      }}
                      className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors"
                    >
                      <p className="text-left">Parent Issue</p>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white truncate">
                          {formData.parentIssue || "Select"}
                        </span>
                        <img src={ChevronDownSm} />
                      </div>
                    </button>

                    <AnimatePresence>
                      {openDropdown === "parentIssue" && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                            dropdownPosition.parentIssue === "top"
                              ? "bottom-full mb-2"
                              : "top-full mt-2"
                          }`}
                        >
                          {/* Search Input */}
                          <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                            <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                              <img src={SearchSm} />
                              <input
                                type="text"
                                placeholder="Search parent issue"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                                autoFocus
                              />
                            </div>
                          </div>

                          {/* Dropdown Items */}
                          <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                            {loadingParentIssues ? (
                              <CircleLoader />
                            ) : (
                              parentIssues
                                .filter(
                                  (issue) =>
                                    issue.key
                                      .toLowerCase()
                                      .includes(searchQuery.toLowerCase()) ||
                                    issue.fields.summary
                                      .toLowerCase()
                                      .includes(searchQuery.toLowerCase())
                                )
                                .map((issue) => (
                                  <button
                                    key={issue.key}
                                    type="button"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        parentIssue: issue.key,
                                      });
                                      setOpenDropdown(null);
                                      setSearchQuery("");
                                    }}
                                    className={`w-full pl-[10px] hover:bg-white/[0.08] pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                      formData.parentIssue === issue.key
                                        ? "bg-gray-500"
                                        : "bg-transparent"
                                    }`}
                                  >
                                    <span className="truncate">
                                      {issue.key} - {issue.fields.summary}
                                    </span>
                                    {formData.parentIssue === issue.key && (
                                      <img src={Checkmark} />
                                    )}
                                  </button>
                                ))
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Priority Selection */}
                <div className="relative shrink-0" ref={priorityDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDropdown(
                        openDropdown === "priority" ? null : "priority"
                      );
                      setSearchQuery("");
                    }}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors"
                  >
                    <p className="text-left">Priority</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white truncate">
                        {formData.priority || "None"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "priority" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.priority === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search priority"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingPriorities ? (
                            <CircleLoader />
                          ) : (
                            priorities
                              .filter((priority) =>
                                priority.name
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase())
                              )
                              .map((priority) => (
                                <button
                                  key={priority.id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      priority: priority.name,
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-white/[0.08] pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.priority === priority.name
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{priority.name}</span>
                                  {formData.priority === priority.name && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Assignee Selection */}
                <div className="relative shrink-0" ref={assigneeDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.projectKey) {
                        toast.error("Please select a project first");
                        return;
                      }
                      setOpenDropdown(
                        openDropdown === "assignee" ? null : "assignee"
                      );
                      setSearchQuery("");
                    }}
                    disabled={!formData.projectKey}
                    className="grid grid-cols-2 w-full h-[32px] items-center text-[14px] leading-[20px] px-3 gap-1.5 rounded-[12px] hover:bg-white/[0.08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <p className="text-left">Assignee</p>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white truncate">
                        {formData.assigneeId
                          ? users.find(
                              (u) => u.accountId === formData.assigneeId
                            )?.displayName
                          : "Unassigned"}
                      </span>
                      <img src={ChevronDownSm} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {openDropdown === "assignee" && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`absolute max-h-[172px] w-[50%] right-0 bg-gray-400 rounded-[16px] overflow-hidden z-50 shadow-2xl ${
                          dropdownPosition.assignee === "top"
                            ? "bottom-full mb-2"
                            : "top-full mt-2"
                        }`}
                      >
                        {/* Search Input */}
                        <div className="p-[4px] border-b-[1px] h-[44px] border-gray-500">
                          <div className="flex items-center gap-1.5 pl-[10px] pr-[12px] py-2">
                            <img src={SearchSm} />
                            <input
                              type="text"
                              placeholder="Search assignee"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="bg-transparent text-white text-[14px] focus:ring-0 focus:outline-none font-medium leading-[20px] fontoutline-none w-full placeholder:text-gray-800"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Dropdown Items */}
                        <div className="overflow-y-auto no-scrollbar p-1 pt-2 flex flex-col gap-[4px] max-h-[128px]">
                          {loadingUsers ? (
                            <CircleLoader />
                          ) : (
                            users
                              .filter((user) =>
                                user.displayName
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase())
                              )
                              .map((user) => (
                                <button
                                  key={user.accountId}
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      assigneeId: user.accountId,
                                    });
                                    setOpenDropdown(null);
                                    setSearchQuery("");
                                  }}
                                  className={`w-full pl-[10px] hover:bg-gray-500 pr-[12px] py-2 text-left rounded-[12px] text-white text-[14px] leading-[20px] transition-colors flex items-center justify-between ${
                                    formData.assigneeId === user.accountId
                                      ? "bg-gray-500"
                                      : "bg-transparent"
                                  }`}
                                >
                                  <span>{user.displayName}</span>
                                  {formData.assigneeId === user.accountId && (
                                    <img src={Checkmark} />
                                  )}
                                </button>
                              ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={
                  creating ||
                  !formData.siteId ||
                  !formData.projectKey ||
                  !formData.issueTypeName ||
                  !formData.summary ||
                  (isSubtask && !formData.parentIssue)
                }
                className=" bg-blue hover:bg-blue/[0.8] rounded-[14px] !h-[44px] shrink-0 text-[14px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Push issue to Jira
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JiraIssueModal;
